/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice } from "@reduxjs/toolkit";
import { REQUEST_STATUS } from "@/ts/enums/RequestStatus";
import { AcademicTranscriptPreviewInterface } from "@/ts/interfaces/Reporting/AcademicTranscriptPreviewInterface";
import {
    downloadAcademicTranscriptThunk,
    fetchAcademicTranscriptPreviewThunk,
    generateAcademicTranscriptThunk,
    getLatestAcademicTranscriptThunk
} from "../thunks";

export interface ReportingSubState<T> {
  data: T;
  status: REQUEST_STATUS;
  error: string | null;
}

// ---- Slice state
interface AcademicTranscriptState {
  preview: ReportingSubState<AcademicTranscriptPreviewInterface | null>;
  generate: ReportingSubState<any | null>;
  latest: ReportingSubState<any | null>;
  download: ReportingSubState<any | null>;
}

const subInitial = <T>(init: T): ReportingSubState<T> => ({
  data: init,
  status: REQUEST_STATUS.IDLE,
  error: null,
});

const initialState: AcademicTranscriptState = {
  preview: subInitial<AcademicTranscriptPreviewInterface | null>(null),
  generate: subInitial<any | null>(null),
  latest: subInitial<any | null>(null),
  download: subInitial<any | null>(null),
};

// =================== SLICE ===================
export const reportingTermAcademicTranscriptSlice = createSlice({
  name: "reporting/TermAcademicTranscript",
  initialState,
  reducers: {
    resetAcademicTranscriptState: () => initialState,
    clearPreview(state) {
      state.preview = subInitial<AcademicTranscriptPreviewInterface | null>(null);
    },
    clearGenerate(state) {
      state.generate = subInitial<any | null>(null);
    },
    clearLatest(state) {
      state.latest = subInitial<any | null>(null);
    },
    clearDownload(state) {
      state.download = subInitial<any | null>(null);
    },
  },
  extraReducers: builder => {
    // preview
    builder
      .addCase(fetchAcademicTranscriptPreviewThunk.pending, state => {
        state.preview.status = REQUEST_STATUS.PENDING;
        state.preview.error = null;
      })
      .addCase(fetchAcademicTranscriptPreviewThunk.fulfilled, (state, action) => {
        state.preview.status = REQUEST_STATUS.SUCCESS;
        state.preview.data = action.payload.data;
      })
      .addCase(fetchAcademicTranscriptPreviewThunk.rejected, (state, action) => {
        state.preview.status = REQUEST_STATUS.ERROR;
        state.preview.error = action.payload ?? "Preview failed";
      });

    // generate
    builder
      .addCase(generateAcademicTranscriptThunk.pending, state => {
        state.generate.status = REQUEST_STATUS.PENDING;
        state.generate.error = null;
      })
      .addCase(generateAcademicTranscriptThunk.fulfilled, (state, action) => {
        state.generate.status = REQUEST_STATUS.SUCCESS;
        state.generate.data = action.payload.data;
      })
      .addCase(generateAcademicTranscriptThunk.rejected, (state, action) => {
        state.generate.status = REQUEST_STATUS.ERROR;
        state.generate.error = action.payload ?? "Generate failed";
      });

    // latest
    builder
      .addCase(getLatestAcademicTranscriptThunk.pending, state => {
        state.latest.status = REQUEST_STATUS.PENDING;
        state.latest.error = null;
      })
      .addCase(getLatestAcademicTranscriptThunk.fulfilled, (state, action) => {
        state.latest.status = REQUEST_STATUS.SUCCESS;
        state.latest.data = action.payload.data;
      })
      .addCase(getLatestAcademicTranscriptThunk.rejected, (state, action) => {
        state.latest.status = REQUEST_STATUS.ERROR;
        state.latest.error = action.payload ?? "Latest fetch failed";
      });

    // download
    builder
      .addCase(downloadAcademicTranscriptThunk.pending, state => {
        state.download.status = REQUEST_STATUS.PENDING;
        state.download.error = null;
      })
      .addCase(downloadAcademicTranscriptThunk.fulfilled, (state, action) => {
        state.download.status = REQUEST_STATUS.SUCCESS;
        state.download.data = action.payload.data;
      })
      .addCase(downloadAcademicTranscriptThunk.rejected, (state, action) => {
        state.download.status = REQUEST_STATUS.ERROR;
        state.download.error = action.payload ?? "Download failed";
      });
  },
});

export const {
  resetAcademicTranscriptState,
  clearPreview,
  clearGenerate,
  clearLatest,
  clearDownload,
} = reportingTermAcademicTranscriptSlice.actions;

export default reportingTermAcademicTranscriptSlice.reducer;
